import { useEffect, useRef, useState } from 'react';
import { AppState, AppStateStatus, Platform } from 'react-native';

export interface AppLifecycle {
  currentState: AppStateStatus;
  isColdStart: boolean;
  lastResumeAt?: number;
}

export const useAppLifecycle = (): AppLifecycle => {
  const [currentState, setCurrentState] = useState<AppStateStatus>(
    AppState.currentState,
  );
  const [isColdStart, setIsColdStart] = useState<boolean>(true);
  const [lastResumeAt, setLastResumeAt] = useState<number | undefined>(
    undefined,
  );

  // Ref mirrors the state value so the listener closure can read it without
  // being listed as an effect dependency (which would cause unnecessary
  // listener teardown/re-registration on each isColdStart state change).
  const isColdStartRef = useRef<boolean>(true);
  const prevStateRef = useRef<AppStateStatus | null>(null);

  useEffect(() => {
    // On web, AppState events do not fire reliably, so immediately mark cold
    // start as complete to prevent the auth cold-start logout from triggering
    // on every page load/refresh.
    if (Platform.OS === 'web') {
      isColdStartRef.current = false;
      setIsColdStart(false);
      return;
    }

    const sub = AppState.addEventListener(
      'change',
      (nextState: AppStateStatus) => {
        const prev = prevStateRef.current;
        prevStateRef.current = nextState;
        setCurrentState(nextState);

        if (isColdStartRef.current) {
          // First event after mount marks end of cold start; skip resume logic.
          isColdStartRef.current = false;
          setIsColdStart(false);
          return;
        }

        const wasBackground = prev === 'background' || prev === 'inactive';
        if (wasBackground && nextState === 'active') {
          setLastResumeAt(Date.now());
        }
      },
    );

    return () => {
      sub.remove();
    };
  }, []); // Stable — isColdStart is read via ref to avoid re-registration

  return { currentState, isColdStart, lastResumeAt };
};
